import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Form } from 'antd';
import DspxModal from '../../../common/DspxModal/DspxModal';
import ModalSubtitle from '../../ModalSubtitle/ModalSubtitle';
import AddTenantForm from '../../forms/AddTenantForm/AddTenantForm';
import addTenant from '../../../../requests/addTenant';
import bindTenant from '../../../../requests/bindTenant';
import { texts } from '../../../../constants/texts';
import ModalFooter from './components/ModalFooter';
import { normalizeVerifiedValue } from '../../../../utils/normalizeVerifiedValue';
import useDeleteTenantComfirm from '../../../../hooks/useDeleteTenantComfirm';

const AddTenantModalNode = ({
  form,
  initialValues,
  onCancel,
  address,
  addressId,
  queryKeys,
  withSelectFlat = false,
  withSelectHouse = false,
  withDeleteBtn = true,
  ...props
}) => {
  const { resetFields, validateFieldsAndScroll } = form;

  const queryClient = useQueryClient();

  const [addConfirmLoading, setAddConfirmLoading] = useState(false);

  const handleCancel = () => {
    resetFields();
    onCancel();
  };

  const handleOkDeleteTenant = () => {
    handleCancel();
  };

  const { handleDeleteTenant: onDeleteTenant, isLoadingDeleteTenant } =
    useDeleteTenantComfirm();

  const handleSubmit = () => {
    validateFieldsAndScroll(async (err, fieldValues) => {
      if (!err) {
        setAddConfirmLoading(true);

        const keys = fieldValues['tenantKeys'];

        await Promise.all(
          keys.map(async (key) => {
            const tenatFio = [
              fieldValues[`surName-${key}`],
              fieldValues[`firstName-${key}`],
              fieldValues[`patrName-${key}`],
            ]
              .filter(Boolean)
              .join(' ')
              .trim();

            const data = {
              ...(!!fieldValues[`surName-${key}`]?.trim()
                ? {
                    surName: fieldValues[`surName-${key}`]?.trim(),
                    ...(!!fieldValues[`firstName-${key}`]?.trim() && {
                      firstName: fieldValues[`firstName-${key}`]?.trim(),
                    }),
                    ...(!!fieldValues[`patrName-${key}`]?.trim() && {
                      patrName: fieldValues[`patrName-${key}`]?.trim(),
                    }),
                  }
                : {
                    ...(!!fieldValues[`fio-${key}`]?.split(' ')?.[0] && {
                      surName: fieldValues[`fio-${key}`]?.split(' ')?.[0],
                    }),
                    ...(!!fieldValues[`fio-${key}`]?.split(' ')?.[1] && {
                      firstName: fieldValues[`fio-${key}`]?.split(' ')?.[1],
                    }),
                    ...(!!fieldValues[`fio-${key}`]?.split(' ')?.[2] && {
                      patrName: fieldValues[`fio-${key}`]?.split(' ')?.[2],
                    }),
                  }),
              fio: tenatFio || fieldValues[`fio-${key}`],
              phone: fieldValues[`phone-${key}`].replace(/\D/g, ''),
              email: fieldValues[`email-${key}`] || '',
              isOwner: fieldValues[`isOwner-${key}`],
              ...(initialValues?.isShowVerified
                ? {
                    isVerified: normalizeVerifiedValue(
                      fieldValues[`isVerified-${key}`]
                    ),
                  }
                : {}),
            };

            if (fieldValues[`id-${key}`]) {
              data.id = fieldValues[`id-${key}`];
            }

            if (initialValues?.bindId) {
              data.bindId = initialValues?.bindId;
            }

            const tenant = await addTenant(
              data,
              tenatFio,
              Boolean(initialValues)
            );

            if (tenant?.id) {
              await bindTenant(
                {
                  addressId:
                    addressId ||
                    fieldValues.flatData.key ||
                    fieldValues.flatData.value,
                  clientId: tenant.id,
                  isOwner: fieldValues[`isOwner-${key}`],
                  ...(initialValues?.isShowVerified
                    ? {
                        isVerified: normalizeVerifiedValue(
                          fieldValues[`isVerified-${key}`]
                        ),
                      }
                    : {}),
                  availableForClient: !!initialValues
                    ? initialValues?.availableForClient
                    : true,
                },
                tenatFio,
                Boolean(initialValues)
              );
            }

            return;
          })
        );

        queryKeys.forEach((key) => queryClient.invalidateQueries(key));
        setAddConfirmLoading(false);
        handleCancel();
      }
    });
  };

  const handleDeleteTenant = () => {
    if (!initialValues?.bindId) {
      return;
    }

    onDeleteTenant({
      tenantId: initialValues?.id,
      tenantBindId: initialValues?.bindId,
      address,
      addressId,
      phone: initialValues?.phone || '',
      onOk: handleOkDeleteTenant,
    });
  };

  return (
    <DspxModal
      title={initialValues ? texts.editTenant : texts.addTenant}
      onCancel={handleCancel}
      onOk={handleSubmit}
      footer={
        <ModalFooter
          initialValues={initialValues}
          addConfirmLoading={addConfirmLoading}
          deleteConfirmLoading={isLoadingDeleteTenant}
          onDelete={withDeleteBtn ? handleDeleteTenant : undefined}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      }
      destroyOnClose
      {...props}
    >
      {address && <ModalSubtitle subtitle={address} />}
      <AddTenantForm
        form={form}
        withSelectFlat={withSelectFlat}
        withSelectHouse={withSelectHouse}
        initialValues={initialValues}
        addressId={addressId}
      />
    </DspxModal>
  );
};

const AddTenantModal = Form.create({ name: 'add_tenant' })(AddTenantModalNode);

export default AddTenantModal;
